import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  useMediaQuery,
  useTheme,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Tooltip
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  AdminPanelSettings,
  Build,
  Visibility,
  Menu as MenuIcon,
  Home as HomeIcon,
  LocalShipping as TruckIcon,
  BusinessCenter as KitIcon,
  Inventory as InventoryIcon,
  Settings as AdminDashboardIcon,
  Feedback as FeedbackIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { getAppSettings } from '../../services/appSettingsService';

// Environment indicator configuration
const getEnvironmentConfig = () => {
  const env = process.env.REACT_APP_ENVIRONMENT || 'development';
  switch (env) {
    case 'development':
      return {
        label: 'DEV',
        color: '#ff9800', // Orange
        show: true,
        warning: 'Not for production use'
      };
    case 'beta':
      return {
        label: 'BETA',
        color: '#2196f3', // Blue
        show: true,
        warning: 'Not for production use'
      };
    case 'production':
      return {
        label: 'PROD',
        color: '#4caf50', // Green
        show: false,
        warning: null
      };
    default:
      return {
        label: env.toUpperCase(),
        color: '#9e9e9e', // Gray
        show: true,
        warning: 'Not for production use'
      };
  }
};

function Header({ user, userRole }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [feedbackSettings, setFeedbackSettings] = useState({ feedbackUrl: '', feedbackButtonText: 'Submit Feedback' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMedium = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Load feedback settings
  useEffect(() => {
    const loadFeedbackSettings = async () => {
      try {
        const settings = await getAppSettings();
        setFeedbackSettings({
          feedbackUrl: settings.feedbackUrl || '',
          feedbackButtonText: settings.feedbackButtonText || 'Submit Feedback'
        });
      } catch (error) {
        console.error('Error loading feedback settings:', error);
      }
    };

    if (user) {
      loadFeedbackSettings();
    }
  }, [user]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleMenuClose();
    await signOut();
  };

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleFeedbackClick = () => {
    if (feedbackSettings.feedbackUrl) {
      window.open(feedbackSettings.feedbackUrl, '_blank', 'noopener,noreferrer');
    }
    handleMenuClose();
    setMobileDrawerOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileDrawerOpen(false); // Close drawer on mobile
  };

  // Navigation menu items
  const getNavigationItems = () => {
    const items = [
      {
        label: 'Dashboard',
        path: '/',
        icon: <HomeIcon />,
        available: true
      },
      {
        label: 'Truck Management',
        path: '/truck-management',
        icon: <TruckIcon />,
        available: true
      },
      {
        label: 'Kit Management',
        path: '/kit-management',
        icon: <KitIcon />,
        available: true,
        adminOnly: false  // Changed to allow all users since Kit Checkoff is available to them
      },
      {
        label: 'Inventory',
        path: '/inventory',
        icon: <InventoryIcon />,
        available: true,
        adminOnly: true  // Only for admins
      },
      {
        label: 'Admin Dashboard',
        path: '/admin',
        icon: <AdminDashboardIcon />,
        available: true,
        adminOnly: true
      }
    ];

    // Filter items based on role
    return items.filter(item => {
      if (item.adminOnly && userRole !== 'ADMIN') {
        return false;
      }
      return true;
    });
  };

  // Role display configuration
  const getRoleConfig = (role) => {
    switch (role) {
      case 'ADMIN':
        return {
          label: 'Admin',
          color: 'error',
          icon: <AdminPanelSettings sx={{ fontSize: 16 }} />
        };
      case 'SUPERVISOR':
        return {
          label: 'Supervisor',
          color: 'warning',
          icon: <Visibility sx={{ fontSize: 16 }} />
        };
      case 'INSPECTOR':
        return {
          label: 'Inspector',
          color: 'success',
          icon: <Build sx={{ fontSize: 16 }} />
        };
      default:
        return {
          label: 'User',
          color: 'default',
          icon: <AccountCircle sx={{ fontSize: 16 }} />
        };
    }
  };

  const roleConfig = getRoleConfig(userRole);

  return (
    <>
    <AppBar 
      position="sticky" 
      elevation={2}
      sx={{
        backgroundColor: '#3C84C2',
        '& .MuiToolbar-root': {
          minHeight: { xs: 56, sm: 64 }
        }
      }}
    >
      <Toolbar>
        {/* Mobile Menu Button */}
        {user && isMobile && (
          <IconButton
            color="inherit"
            aria-label="open navigation menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo and Title Section - Clickable */}
        <Box 
          onClick={() => user && navigate('/')}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: { xs: 1, md: 0 },
            minWidth: { md: '300px' }, // Ensure minimum space for title
            maxWidth: { md: '400px' }, // Prevent excessive growth
            cursor: user ? 'pointer' : 'default',
            '&:hover': user ? {
              opacity: 0.9
            } : {}
          }}
        >
          {/* CCRS Logo */}
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
            <img
              src="/ccrs-logo.webp"
              alt="CCRS"
              style={{
                height: isMobile ? '32px' : '40px',
                width: 'auto'
                // Removed filter - logo already has proper colors
              }}
              onError={(e) => {
                // Fallback to text if image fails
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <Typography
              variant="h6"
              component="span"
              sx={{
                display: 'none', // Hidden by default, shown if image fails
                fontWeight: 700,
                color: 'white'
              }}
            >
              CCRS
            </Typography>
          </Box>

          {/* App Title */}
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              component="h1"
              sx={{
                fontWeight: 600,
                color: 'white',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                userSelect: 'none' // Prevent text selection on click
              }}
            >
{isMobile ? "Operations" : isMedium ? "CCRS Operations" : "CCRS Operations Center"}
              {/* Environment Indicator */}
              {getEnvironmentConfig().show && (
                <Chip
                  label={getEnvironmentConfig().label}
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    backgroundColor: getEnvironmentConfig().color,
                    color: 'white',
                    verticalAlign: 'middle',
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              )}
            </Typography>
            {!isMobile && (
              <Typography
                variant="caption"
                sx={{
                  color: getEnvironmentConfig().warning ? '#ffeb3b' : 'rgba(255, 255, 255, 0.8)',
                  display: 'block',
                  lineHeight: 1,
                  userSelect: 'none', // Prevent text selection on click
                  fontWeight: getEnvironmentConfig().warning ? 600 : 400
                }}
              >
                {getEnvironmentConfig().warning || 'Fleet & Operations Management'}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Desktop Navigation */}
        {user && !isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, flexGrow: 1, justifyContent: 'flex-end' }}>
            {getNavigationItems().map((item) => {
              const isActive = location.pathname === item.path;
              const button = (
                <Button
                  key={item.path}
                  startIcon={!isMedium ? item.icon : null}
                  onClick={() => handleNavigation(item.path)}
                  disabled={!item.available}
                  sx={{
                    color: 'white',
                    mx: isMedium ? 0.5 : 1,
                    px: isMedium ? 1 : 2,
                    py: 1,
                    minWidth: isMedium ? 'auto' : 'initial',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: isActive ? 600 : 400,
                    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    border: isActive ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid transparent',
                    opacity: item.available ? 1 : 0.5,
                    '&:hover': item.available ? {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    } : {},
                    '&:disabled': {
                      color: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                >
                  {isMedium ? (
                    // Icon only on medium screens
                    item.icon
                  ) : (
                    // Full label on large screens
                    <>
                      {item.label}
                      {!item.available && (
                        <Chip
                          label="Soon"
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white'
                          }}
                        />
                      )}
                    </>
                  )}
                </Button>
              );
              
              // Wrap in tooltip for medium screens
              return isMedium ? (
                <Tooltip 
                  key={item.path}
                  title={
                    item.available ? item.label : `${item.label} - Coming Soon`
                  }
                  arrow
                  placement="bottom"
                >
                  {button}
                </Tooltip>
              ) : (
                button
              );
            })}
          </Box>
        )}

        {/* User Section */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* User Menu Button */}
            <IconButton
              size="large"
              edge="end"
              aria-label="user menu"
              aria-controls="user-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              sx={{ color: 'white' }}
            >
              {user.photoURL ? (
                <Avatar
                  src={user.photoURL}
                  alt={user.displayName || user.email}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <AccountCircle />
              )}
            </IconButton>

            {/* User Menu */}
            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  minWidth: 220,
                  mt: 1
                }
              }}
            >
              {/* User Info */}
              <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" noWrap>
                  {user.displayName || 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user.email}
                </Typography>
                {userRole && userRole !== 'DENIED' && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={roleConfig.label}
                      color={roleConfig.color}
                      size="small"
                      icon={roleConfig.icon}
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>

              {/* Feedback Option */}
              {feedbackSettings.feedbackUrl && (
                <MenuItem onClick={handleFeedbackClick}>
                  <FeedbackIcon sx={{ mr: 2 }} />
                  {feedbackSettings.feedbackButtonText}
                </MenuItem>
              )}

              {/* Sign Out Option */}
              <MenuItem onClick={handleSignOut}>
                <ExitToApp sx={{ mr: 2 }} />
                Sign Out
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>

    {/* Mobile Navigation Drawer */}
    {user && (
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: '#f8f9fa'
          },
        }}
      >
        <Box sx={{ overflow: 'auto' }}>
          {/* Drawer Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#3C84C2',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src="/ccrs-logo.webp"
                alt="CCRS"
                style={{ height: '32px', width: 'auto' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Operations Center
                </Typography>
                {getEnvironmentConfig().show && (
                  <Chip
                    label={getEnvironmentConfig().label}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      backgroundColor: getEnvironmentConfig().color,
                      color: 'white',
                      '& .MuiChip-label': {
                        px: 0.75
                      }
                    }}
                  />
                )}
              </Box>
              {getEnvironmentConfig().warning ? (
                <Typography variant="caption" sx={{ color: '#ffeb3b', fontWeight: 600 }}>
                  {getEnvironmentConfig().warning}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {user.displayName || user.email?.split('@')[0] || 'User'}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Navigation Items */}
          <List sx={{ pt: 1 }}>
            {getNavigationItems().map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    disabled={!item.available}
                    sx={{
                      py: 1.5,
                      px: 2,
                      backgroundColor: isActive ? 'rgba(60, 132, 194, 0.1)' : 'transparent',
                      borderRight: isActive ? '4px solid #3C84C2' : '4px solid transparent',
                      '&:hover': item.available ? {
                        backgroundColor: 'rgba(60, 132, 194, 0.05)'
                      } : {}
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: isActive ? '#3C84C2' : item.available ? 'text.primary' : 'text.disabled'
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: isActive ? 600 : 400,
                              color: isActive ? '#3C84C2' : item.available ? 'text.primary' : 'text.disabled'
                            }}
                          >
                            {item.label}
                          </Typography>
                          {!item.available && (
                            <Chip
                              label="Soon"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                color: 'text.secondary'
                              }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ mt: 2 }} />

          {/* User Info in Drawer */}
          <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              {user.photoURL ? (
                <Avatar
                  src={user.photoURL}
                  alt={user.displayName || user.email}
                  sx={{ width: 40, height: 40 }}
                />
              ) : (
                <Avatar sx={{ width: 40, height: 40, backgroundColor: '#3C84C2' }}>
                  <AccountCircle />
                </Avatar>
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {user.displayName || 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user.email}
                </Typography>
              </Box>
            </Box>
            
            {userRole && userRole !== 'DENIED' && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={getRoleConfig(userRole).label}
                  color={getRoleConfig(userRole).color}
                  size="small"
                  icon={getRoleConfig(userRole).icon}
                  variant="outlined"
                />
              </Box>
            )}

            {feedbackSettings.feedbackUrl && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<FeedbackIcon />}
                onClick={handleFeedbackClick}
                sx={{ mt: 1 }}
              >
                {feedbackSettings.feedbackButtonText}
              </Button>
            )}

            <Button
              fullWidth
              variant="outlined"
              startIcon={<ExitToApp />}
              onClick={handleSignOut}
              sx={{ mt: 1 }}
            >
              Sign Out
            </Button>
          </Box>
        </Box>
      </Drawer>
    )}
  </>
  );
}

export default Header;